export function mapMaterialOrientation(width, height) {
    if (!width || !height)
        return 'Unknown';
    if (width > height)
        return 'Landscape';
    if (height > width)
        return 'Portrait';
    return 'Square';
}
/**
 * Efkt types by media, datahub by product — see `MaterialAssetType` on the model. `Thumbnail` and
 * `Html` have no datahub counterpart at all, so they collapse to `'Unknown'` rather than to a wrong
 * guess.
 */
export function mapDeliverableTypeToMaterialAssetType(type) {
    switch (type) {
        case 'Image':
            return 'Picture';
        case 'Video':
            return 'Video';
        case 'Url':
            return 'Link';
        case 'Text':
        case 'Html':
        case 'Thumbnail':
        default:
            return 'Unknown';
    }
}
function mapDeliverableTypeToSourceType(type) {
    if (type === 'Video')
        return 'Video';
    if (type === 'Url')
        return 'Link';
    return 'Image';
}
function mapDeliveredAssetFromDto(asset, delivery, group, sortOrder) {
    var _a;
    return {
        batchId: group.groupId,
        orderlineId: delivery.orderLineId,
        // No write endpoint exists for the description, so there is nothing to let the user edit.
        canEditDescription: false,
        fileName: asset.fileName,
        id: asset.assetId,
        // Everything the deliveries read returns has already been delivered — defaulting to `false`
        // would hide every asset rather than signal "no data".
        isActive: true,
        sortOrder,
        source: asset.url,
        largestSource: asset.url,
        thumbnailUrl: asset.url,
        sourceOrientation: mapMaterialOrientation(asset.width, asset.height),
        thumbnailOrientation: mapMaterialOrientation(asset.width, asset.height),
        sourceType: mapDeliverableTypeToSourceType(asset.type),
        type: mapDeliverableTypeToMaterialAssetType(asset.type),
        updatedDate: delivery.deliveredAt ? new Date(delivery.deliveredAt) : undefined,
        before: '',
        after: '',
        // Optional-chained by their consumers with no further guard (`MaterialCarouselModal`,
        // `CloudScannerLinksModal`) — `undefined` would throw on `.length`/`.map`.
        links: [],
        cloudScannerLinks: [],
        isSelected: false,
        isIFPMaterial: false,
        vendorOrderLineId: (_a = delivery.purchaseOrder) === null || _a === void 0 ? void 0 : _a.externalOrderLineId,
    };
}
/**
 * Walks `order → group → delivery → deliveredAssets[]` into the flat list booqly's material grid
 * reads, plus the `batchList`/`links` siblings `MaterialApi.getSimplifiedVisualMaterials` mirrors
 * from datahub. `sortOrder` is assigned by flatten order since Efkt has no persisted ordering and,
 * critically, no write endpoint for one — so any client-side re-sort is local-only and must not be
 * wired to a save call.
 */
export function flattenCaseDeliveries(orders) {
    const materials = [];
    const batchList = [];
    let sortOrder = 0;
    orders.forEach(order => {
        order.groups.forEach(group => {
            const products = [];
            group.deliveries.forEach(delivery => {
                products.push({
                    orderId: order.id,
                    orderLineId: delivery.orderLineId,
                    productName: delivery.productName,
                });
                (delivery.deliveredAssets || []).forEach(asset => {
                    materials.push(mapDeliveredAssetFromDto(asset, delivery, group, sortOrder));
                    sortOrder += 1;
                });
            });
            if (products.length) {
                batchList.push({
                    batchId: group.groupId,
                    orderLineId: products[0].orderLineId,
                    products,
                });
            }
        });
    });
    // No cloud-scanner / video-link source exists at the case-deliveries level — `[]`, not
    // `undefined`, matching the same "never optional-chained" rule the assets above follow.
    return { materials, batchList, links: [], sourceBackend: 'efkt' };
}
export function mapOrderLineAssetFromDto(dto, orderLineId) {
    return {
        id: dto.assetId,
        orderlineId: orderLineId,
        fileName: dto.fileName,
        url: dto.url,
    };
}
/** EFKT-MOCK. See `models.ts::ILinkOrDocumentMaterial`. */
export function mapLinkOrDocumentMaterialFromDto(dto) {
    return { id: dto.id, name: dto.name, url: dto.url };
}
/** EFKT-MOCK. See `models.ts::ICollagePrice`. */
export function mapCollagePriceFromDto(dto) {
    return { ...dto };
}
